import type { AxiosInstance } from '@glovo/fetcher'
import { zxcvbn } from '@zxcvbn-ts/core'
import { cache } from 'react'
import { PASSWORD_MIN_LENGTH, PasswordStrength } from './constants'
import { getConfig } from '@/tools/config'
import { trackRegistration } from '@/tools/device-detection/providers/incognia'
import { fetcher } from '@/tools/fetcher/client'
import type { CompanyDetails, CreateUserPayload, UserModel, UserPassword } from '@/user/entities'

export const getPasswordStrength = (password: UserPassword): PasswordStrength => {
  const passwordScore = zxcvbn(password).score

  if (!password || password.length === 0) {
    return PasswordStrength.EMPTY_PASSWORD
  }

  if (password.length < PASSWORD_MIN_LENGTH) {
    return PasswordStrength.SHORT_PASSWORD
  }

  switch (passwordScore) {
    case 0:
      return PasswordStrength.WEAK_PASSWORD
    case 1:
      return PasswordStrength.WEAK_PASSWORD
    case 2:
      return PasswordStrength.DECENT_PASSWORD
    case 3:
      return PasswordStrength.STRONG_PASSWORD
    case 4:
      return PasswordStrength.STRONGEST_PASSWORD
    default:
      throw new Error('[getPasswordStrength] Unknown password strength')
  }
}

export const isStrongEnoughPassword = (password: UserPassword): boolean => {
  const passwordStrength = getPasswordStrength(password)
  return (
    passwordStrength === PasswordStrength.DECENT_PASSWORD ||
    passwordStrength === PasswordStrength.STRONG_PASSWORD ||
    passwordStrength === PasswordStrength.STRONGEST_PASSWORD
  )
}

export const fetchUserData = cache(async (fetcherInstance: AxiosInstance = fetcher) => {
  const { apiUrl } = getConfig()
  const response = await fetcherInstance.get<UserModel>(`${apiUrl}/v3/me`)
  return response.data as UserModel
})

export const updateUserData = async (
  newUser: { name?: string; email?: string },
  urn: string | undefined
) => {
  const { apiUrl } = getConfig()
  const response = await fetcher.put(`${apiUrl}/v3/users/${urn}`, newUser)
  return response.data
}

export const updatePassword = async (
  password: {
    currentPassword: string
    newPassword: string
  },
  urn: string | undefined
) => {
  const { apiUrl } = getConfig()
  const response = await fetcher.put(`${apiUrl}/v3/users/${urn}/password`, password)
  return response.data
}

export const createCompanyDetails = async (
  id: number | undefined,
  companyDetails: CompanyDetails
) => {
  const { apiUrl } = getConfig()
  const response = await fetcher.put(`${apiUrl}/v3/customers/${id}/companydetail`, companyDetails)
  return response.data
}

export const deleteCompanyDetails = async (id: number | undefined) => {
  const { apiUrl } = getConfig()
  await fetcher.delete(`${apiUrl}/v3/customers/${id}/companydetail`)
}

export const createUser = async (
  { email, name, token, grantType, facebookId, phoneNumber }: CreateUserPayload,
  preferredLanguage = 'en',
  { headers }: { headers: Record<string, string> } = { headers: {} }
) => {
  const { apiUrl } = getConfig()
  const createUserPayload = getCreateUserPayload({
    email,
    name,
    token,
    facebookId,
    preferredLanguage,
    grantType,
    phoneNumber,
  })

  const apiVersion = grantType === 'phone' ? 'v4' : 'v3'
  const user = await fetcher.post<UserModel>(
    `${apiUrl}/${apiVersion}/users/customer`,
    createUserPayload,
    {
      headers: { ...headers },
    }
  )
  void trackRegistration(user.data?.id, phoneNumber as UserModel['phoneNumber'])
}

const getCreateUserPayload = ({
  email,
  name,
  token,
  preferredLanguage,
  facebookId,
  grantType,
  phoneNumber,
}: CreateUserPayload) => {
  const createUserPayload = {
    email,
    name,
    preferredLanguage,
    mediaCampaign: 'Organic Web',
    mediaSource: 'Organic Web',
    privacySettings: ['DATA_POLICY'],
    shownPermissions: ['DATA_POLICY'],
  }
  switch (grantType) {
    case 'google':
      return {
        ...createUserPayload,
        analyticsId: 0,
        googleToken: token,
        type: 'Customer',
      }
    case 'googleBearer':
      return {
        ...createUserPayload,
        analyticsId: 0,
        googleBearerToken: token,
        type: 'Customer',
      }
    case 'facebook':
      return {
        ...createUserPayload,
        analyticsId: 0,
        facebookId,
        facebookToken: token,
        type: 'Customer',
      }
    case 'phone':
      return {
        ...createUserPayload,
        phoneNumber,
        identityFlowId: token,
        email,
        name,
      }
    default:
      const isPasswordStrongEnough = isStrongEnoughPassword(token ? token : '')
      if (!isPasswordStrongEnough) {
        throw new Error('Password strength is not sufficient')
      }
      return {
        ...createUserPayload,
        password: token,
      }
  }
}

export const getUserCountry = async (cityCode: string): Promise<string> => {
  const { apiUrl } = getConfig()
  const response = await fetcher.get(`${apiUrl}/v3/customers/cities?city_code=${cityCode}`)
  return response.data.city.countryCode.toLowerCase()
}

export const getUserPrimeSubscriptionStatus = async (
  customerId: number,
  fetcherInstance: AxiosInstance = fetcher
): Promise<boolean> => {
  const { apiUrl } = getConfig()
  const response = await fetcherInstance.get(
    `${apiUrl}/customers/${customerId}/subscription/status`
  )
  return response.data.isSubscribed
}

export const isStaffUser = (email: unknown) => {
  if (typeof email !== 'string') {
    return false
  } else {
    return email.endsWith('@glovoapp.com') || email.endsWith('@providers.glovoapp.com')
  }
}
